import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { EnterpriseGate } from "@/components/EnterpriseGate";
import { ProtectedRoute, PublicOnlyRoute } from "@/components/auth/RouteGuards";
import RoleRoute from "@/components/auth/RoleRoute";
import FirstExperience from "@/pages/FirstExperience";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Products from "@/pages/Products";
import Suppliers from "@/pages/Suppliers";
import Invoices from "@/pages/Invoices";
import PCF from "@/pages/PCF";
import UnifiedAccounting from "@/pages/UnifiedAccounting";
import LivingPCF from "@/pages/LivingPCF";
import CFODashboard from "@/pages/CFODashboard";
import CPODashboard from "@/pages/CPODashboard";
import CSODashboard from "@/pages/CSODashboard";
import Team from "@/pages/Team";
import SettingsPage from "@/pages/Settings";
import About from "@/pages/About";
import SystemTest from "@/pages/SystemTest";
import News from "@/pages/News";
import AdminNews from "@/pages/AdminNews";
import DATEVIntegration from "@/pages/DATEVIntegration";
import DATEVHelp from "@/pages/DATEVHelp";
import SAPIntegration from "@/pages/SAPIntegration";
import ImportData from "@/pages/ImportData";
import MappingProfiles from "@/pages/MappingProfiles";
import ApiKeys from "@/pages/ApiKeys";
import ApiKeysUsage from "@/pages/ApiKeysUsage";
import Transparency from "@/pages/Transparency";
import ChangeLogs from "@/pages/ChangeLogs";
import TuvDocs from "@/pages/TuvDocs";
import ICS2Generator from "@/pages/ICS2Generator";
import Shipments from "@/pages/Shipments";
import Pricing from "@/pages/Pricing";
import Developers from "@/pages/Developers";
import Feedback from "@/pages/Feedback";
import Contact from "@/pages/Contact";
import AdminRateLimits from "@/pages/AdminRateLimits";
import AdminEmailLogs from "@/pages/AdminEmailLogs";
import PilotRequest from "@/pages/PilotRequest";
import PilotConsultation from "@/pages/PilotConsultation";
import Workspace from "@/pages/Workspace";
import PilotQualification from "@/pages/PilotQualification";
import PilotSuccess from "@/pages/PilotSuccess";
import PilotCancel from "@/pages/PilotCancel";
import SimpleDashboard from "@/pages/SimpleDashboard";
import Methodology from "@/pages/Methodology";
import AcceptInvitation from "@/pages/supplier/AcceptInvitation";
import SupplierDashboard from "@/pages/supplier/Dashboard";
import SupplierInbox from "@/pages/SupplierInbox";
import AuditFeed from "@/pages/AuditFeed";
import TrustCenter from "@/pages/TrustCenter";
import EnterpriseReports from "@/pages/EnterpriseReports";
import MFASetup from "@/pages/MFASetup";
import SecurityActivity from "@/pages/SecurityActivity";
import AIAutomation from "@/pages/AIAutomation";
import Terms from "@/pages/legal/Terms";
import Privacy from "@/pages/legal/Privacy";
import DPA from "@/pages/legal/DPA";
import CookiePolicy from "@/pages/legal/CookiePolicy";
import CookieConsent from "@/components/CookieConsent";

function App() {
  return (
    <BrowserRouter>
      <CookieConsent />
      <Routes>
        <Route path="/login" element={<PublicOnlyRoute><Login /></PublicOnlyRoute>} />
        <Route path="/register" element={<PublicOnlyRoute><Register /></PublicOnlyRoute>} />
        <Route path="/forgot-password" element={<PublicOnlyRoute><ForgotPassword /></PublicOnlyRoute>} />
        <Route path="/reset-password" element={<PublicOnlyRoute><ResetPassword /></PublicOnlyRoute>}/>
        <Route path="/setup" element={<ProtectedRoute><RoleRoute><FirstExperience /></RoleRoute></ProtectedRoute>} />
        <Route path="/supplier/accept/:token" element={<AcceptInvitation />} />
        <Route path="/supplier" element={
          <ProtectedRoute>
            <RoleRoute roles={['supplier']}><SupplierDashboard /></RoleRoute>
          </ProtectedRoute>
        } />
        <Route path="/transparency" element={<Transparency />} />
        <Route path="/legal/terms" element={<Terms />} />
        <Route path="/legal/privacy" element={<Privacy />} />
        <Route path="/legal/dpa" element={<DPA />} />
        <Route path="/legal/cookies" element={<CookiePolicy />} />
        <Route path="/pilot-request" element={<PilotRequest />} />
        <Route path="/pilot-consultation" element={<PilotConsultation />} />
        <Route path="/pilot-qualification" element={<PilotQualification />} />
        <Route path="/pilot/success" element={<PilotSuccess />} />
        <Route path="/pilot/cancel" element={<PilotCancel />} />

        <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
          <Route path="/" element={<Dashboard />} />
          <Route path="/simple-dashboard" element={<SimpleDashboard />} />
          <Route path="/workspace" element={<RoleRoute><Workspace /></RoleRoute>} />
          <Route path="/methodology" element={<Methodology />} />
          <Route path="/products" element={<Products />} />
          <Route path="/suppliers" element={<Suppliers />} />
          <Route path="/suppliers/inbox" element={
            <RoleRoute roles={['admin', 'editor', 'auditor']}><SupplierInbox /></RoleRoute>
          } />
          <Route path="/audit" element={
            <RoleRoute roles={['admin', 'editor', 'auditor']}><AuditFeed /></RoleRoute>
          } />
          <Route path="/trust-center" element={
            <RoleRoute roles={['admin', 'editor', 'auditor']}><TrustCenter /></RoleRoute>
          } />
          <Route path="/enterprise-reports" element={
            <RoleRoute roles={['admin', 'editor', 'auditor']}><EnterpriseReports /></RoleRoute>
          } />
          <Route path="/ai-automation" element={
            <RoleRoute roles={['admin', 'editor']}><AIAutomation /></RoleRoute>
          } />
          <Route path="/security-activity" element={
            <RoleRoute roles={['admin']}><SecurityActivity /></RoleRoute>
          } />
          <Route path="/mfa-setup" element={<MFASetup />} />
          <Route path="/invoices" element={<Invoices />} />
          <Route path="/pcf" element={<PCF />} />
          <Route path="/unified-accounting" element={<UnifiedAccounting />} />
          <Route path="/living-pcf" element={<EnterpriseGate minTier="advanced"><LivingPCF /></EnterpriseGate>} />
          <Route path="/cfo-dashboard" element={<EnterpriseGate minTier="pro"><CFODashboard /></EnterpriseGate>} />
          <Route path="/cpo-dashboard" element={<EnterpriseGate minTier="pro"><CPODashboard /></EnterpriseGate>} />
          <Route path="/cso-dashboard" element={<EnterpriseGate minTier="pro"><CSODashboard /></EnterpriseGate>} />
          <Route path="/team" element={<Team />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/settings/api-keys" element={<ApiKeys />} />
          <Route path="/settings/api-keys/usage" element={<ApiKeysUsage />} />
          <Route path="/change-logs" element={<ChangeLogs />} />
          <Route path="/tuv-docs" element={<TuvDocs />} />
          <Route path="/ics2-generator" element={<ICS2Generator />} />
          <Route path="/shipments" element={<EnterpriseGate allowedTiers={['starter', 'pro', 'advanced', 'enterprise']}><Shipments /></EnterpriseGate>} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/developers" element={<Developers />} />
          <Route path="/feedback" element={<Feedback />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/about" element={<About />} />
          <Route path="/news" element={<News />} />
          <Route path="/admin/system-test" element={<SystemTest />} />
          <Route path="/admin/news" element={<AdminNews />} />
          <Route path="/admin/rate-limits" element={<AdminRateLimits />} />
          <Route path="/admin/email-logs" element={<AdminEmailLogs />} />
          <Route path="/settings/datev" element={<EnterpriseGate><DATEVIntegration /></EnterpriseGate>} />
          <Route path="/settings/sap-integration" element={<EnterpriseGate><SAPIntegration /></EnterpriseGate>} />
          <Route path="/settings/import-data" element={<EnterpriseGate><ImportData /></EnterpriseGate>} />
          <Route path="/settings/mapping-profiles" element={<EnterpriseGate><MappingProfiles /></EnterpriseGate>} />
          <Route path="/help/datev-integration" element={<DATEVHelp />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
